import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import styled from 'styled-components';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button, Heading } from '@savgroup-front-common/core';
import { Modal, MODAL_SIZES } from '@savgroup-front-common/core/src/atoms/modal';

const $Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.85rem;
  text-align: center;
`;

const $Description = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: #475569;
`;

const $QrImage = styled.img`
  display: block;
  width: min(100%, 260px);
  height: auto;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
`;

const $UrlHint = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.35;
  color: #64748b;
  word-break: break-all;
`;

const $ErrorMessage = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #b91c1c;
`;

interface InventoryMobileQrModalProps {
  isOpen: boolean;
  mobileUrl: string;
  onClose: () => void;
}

export const InventoryMobileQrModal = ({
  isOpen,
  mobileUrl,
  onClose,
}: InventoryMobileQrModalProps) => {
  const [qrDataUrl, setQrDataUrl] = useState<string | undefined>();
  const [hasQrError, setHasQrError] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setQrDataUrl(undefined);
      setHasQrError(false);

      return undefined;
    }

    let isCancelled = false;

    void QRCode.toDataURL(mobileUrl, {
      width: 280,
      margin: 2,
      errorCorrectionLevel: 'M',
    })
      .then((dataUrl) => {
        if (!isCancelled) {
          setQrDataUrl(dataUrl);
          setHasQrError(false);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setHasQrError(true);
          setQrDataUrl(undefined);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [isOpen, mobileUrl]);

  return (
    <Modal isOpen={isOpen} size={MODAL_SIZES.SMALL} onClose={onClose}>
      <Heading level={3}>Ouvrir sur mobile</Heading>
      <$Body>
        <$Description>
          Scannez ce QR code avec votre téléphone pour ouvrir cet inventaire en vue mobile (même
          session si vous êtes déjà connecté sur l’appareil).
        </$Description>
        {hasQrError ? (
          <$ErrorMessage>Impossible de générer le QR code.</$ErrorMessage>
        ) : qrDataUrl ? (
          <$QrImage src={qrDataUrl} alt="QR code vers la fiche inventaire mobile" />
        ) : (
          <$Description>Génération du QR code…</$Description>
        )}
        <$UrlHint>{mobileUrl}</$UrlHint>
        <Button type={BUTTON_TYPES.BUTTON} tertiary onClick={onClose}>
          Fermer
        </Button>
      </$Body>
    </Modal>
  );
};
